import { Input } from "antd";
import { FC, useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useDataList from "@/hooks/useDataList";
import api from "@/apis/supplier";
import { CommonListParams, CommonStateEnum } from "@/types/common";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import { Supplier } from "@/types/purchase";
import SText from "@/styles/SText";
import { CountrySelect, CurrencySelect, CreditLevelSelect } from "@/components/Selector";
import STableLayout, { TableLayoutRef, DEFAULT_PAGE_SIZE } from "@/styles/STableLayout";

interface SupplierListProps {
  disUrl?: boolean;
  value?: Supplier | null;
  onChange: (supplier: Supplier) => void;
}

const SupplierList: FC<SupplierListProps> = ({ disUrl = false, value, onChange }) => {
  const { t } = useTranslation();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const [keyword, setKeyword] = useState<string>("");
  const [list, total, listLoading, { fetchList }] = useDataList<Supplier, [CommonListParams, CommonStateEnum]>({
    fetchApi: api.list,
  });
  const [selectedId, setSelectedId] = useState<string | undefined>(value?.id);

  useEffect(() => {
    fetch("", 0);
  }, []);

  useEffect(() => {
    setSelectedId(value?.id);
  }, [value]);

  const fetch = (keyword: string, offset: number) => {
    fetchList({
      body: keyword,
      pagination: disUrl ? { offset, take: tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE } : undefined,
    }, CommonStateEnum.ACTIVE);
  };

  const columns: ColumnsType<Supplier> = [
    {
      title: t("purchase.code"),
      dataIndex: "code",
      width: 100,
      render: (_, item) => <SText text={item.code ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("purchase.name"),
      dataIndex: "name",
      width: 150,
      render: (_, item) => <SText text={item.name ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("purchase.shortName"),
      dataIndex: "shortName",
      width: 100,
      render: (_, item) => <SText text={item.shortName ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("purchase.country"),
      dataIndex: "country",
      width: 100,
      render: (val, item) => <CountrySelect key={item.id} value={val || ""} readOnly={true} />,
    },
    {
      title: t("purchase.contact"),
      dataIndex: "contact",
      width: 100,
      render: (_, item) => <SText text={item.contact ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("purchase.phone"),
      dataIndex: "phone",
      width: 120,
      render: (_, item) => <SText text={item.phone ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("purchase.currency"),
      dataIndex: "currency",
      width: 80,
      render: (val, item) => <CurrencySelect key={item.id} value={val || ""} readOnly={true} />,
    },
    {
      title: t("purchase.creditLevel"),
      dataIndex: "creditLevel",
      width: 100,
      render: (val, item) => <CreditLevelSelect value={val} readOnly key={item.id + "creditLevel"} />,
    },
    {
      title: t("purchase.address"),
      dataIndex: "address",
      width: 200,
      render: (_, item) => <SText text={item.address ?? ""} style={{ width: "100%" }} />,
    },
  ];

  const dataList = useMemo(() => {
    // 验证 wmsApi 是否为有效的 URL
    const urlPattern = /^https?:\/\/.+/i;
    return list.filter(item => (item.wmsApi && urlPattern.test(item.wmsApi)) || disUrl)
  }, [list, disUrl])

  const headerChildren = (
    <Input.Search
      placeholder={t("purchase.bySearchSupplier")}
      style={{ width: 300 }}
      value={keyword}
      onChange={(e) => setKeyword(e.target.value.trim())}
      onSearch={(e) => {
        fetch(e, 0);
        tableRef.current?.resetTablePage();
      }}
    />
  );

  return (
    <STableLayout
      loading={listLoading}
      dataSource={dataList}
      columns={columns as ColumnsType<AnyObject>}
      scrollX={1000}
      headerChildren={headerChildren}
      tableLayoutRef={tableRef}
      total={total}
      handleChange={(offset) => {
        disUrl && fetch(keyword, offset);
      }}
      rowSelection={{
        type: "radio",
        selectedRowKeys: selectedId ? [selectedId] : [],
        onChange: (selectedRowKeys, selectedRows) => {
          const selected = selectedRows[0] as Supplier;
          setSelectedId(selected.id);
          onChange(selected);
        },
      }}
    />
  );
};

export default SupplierList;
